/**
 * Bank Store
 * Zustand store for vendor bank account state management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { BankAccountResponse } from '@/types/api/bank';

/**
 * Bank pagination state
 */
export interface BankPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Bank store state
 */
export interface BankState {
  // Data - keyed by vendorId
  bankAccountsByVendor: Record<number, BankAccountResponse[]>;
  currentBankAccount: BankAccountResponse | null;

  // Pagination - keyed by vendorId
  paginationByVendor: Record<number, BankPagination>;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Modal state
  isBankModalOpen: boolean;
  editingBankId: number | null;
  currentVendorId: number | null;

  // Actions - Data
  setBankAccountsForVendor: (vendorId: number, accounts: BankAccountResponse[]) => void;
  setCurrentBankAccount: (account: BankAccountResponse | null) => void;
  addBankAccount: (vendorId: number, account: BankAccountResponse) => void;
  updateBankAccount: (vendorId: number, bankId: number, updates: Partial<BankAccountResponse>) => void;
  removeBankAccount: (vendorId: number, bankId: number) => void;
  setDefaultBankAccount: (vendorId: number, bankId: number) => void;
  clearBankAccountsForVendor: (vendorId: number) => void;

  // Actions - Pagination
  setPaginationForVendor: (vendorId: number, pagination: Partial<BankPagination>) => void;

  // Actions - UI State
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Modal
  openBankModal: (vendorId: number, bankId?: number) => void;
  closeBankModal: () => void;

  // Actions - Reset
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  bankAccountsByVendor: {},
  currentBankAccount: null,
  paginationByVendor: {},
  isLoading: false,
  error: null,
  isBankModalOpen: false,
  editingBankId: null,
  currentVendorId: null,
};

/**
 * Default pagination
 */
const defaultPagination: BankPagination = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

/**
 * Bank Zustand store
 */
export const useBankStore = create<BankState>()(
  devtools(
    (set) => ({
      ...initialState,

      // Data actions
      setBankAccountsForVendor: (vendorId, accounts) =>
        set(
          (state) => ({
            bankAccountsByVendor: {
              ...state.bankAccountsByVendor,
              [vendorId]: accounts,
            },
          }),
          false,
          'bank/setBankAccountsForVendor'
        ),

      setCurrentBankAccount: (account) =>
        set({ currentBankAccount: account }, false, 'bank/setCurrentBankAccount'),

      addBankAccount: (vendorId, account) =>
        set(
          (state) => ({
            bankAccountsByVendor: {
              ...state.bankAccountsByVendor,
              [vendorId]: [
                account,
                ...(state.bankAccountsByVendor[vendorId] || []),
              ],
            },
          }),
          false,
          'bank/addBankAccount'
        ),

      updateBankAccount: (vendorId, bankId, updates) =>
        set(
          (state) => ({
            bankAccountsByVendor: {
              ...state.bankAccountsByVendor,
              [vendorId]: (state.bankAccountsByVendor[vendorId] || []).map((b) =>
                b.bankId === bankId ? { ...b, ...updates } : b
              ),
            },
          }),
          false,
          'bank/updateBankAccount'
        ),

      removeBankAccount: (vendorId, bankId) =>
        set(
          (state) => ({
            bankAccountsByVendor: {
              ...state.bankAccountsByVendor,
              [vendorId]: (state.bankAccountsByVendor[vendorId] || []).filter(
                (b) => b.bankId !== bankId
              ),
            },
          }),
          false,
          'bank/removeBankAccount'
        ),

      setDefaultBankAccount: (vendorId, bankId) =>
        set(
          (state) => ({
            bankAccountsByVendor: {
              ...state.bankAccountsByVendor,
              [vendorId]: (state.bankAccountsByVendor[vendorId] || []).map((b) => ({
                ...b,
                isDefault: b.bankId === bankId,
              })),
            },
          }),
          false,
          'bank/setDefaultBankAccount'
        ),

      clearBankAccountsForVendor: (vendorId) =>
        set(
          (state) => {
            const { [vendorId]: _, ...rest } = state.bankAccountsByVendor;
            return { bankAccountsByVendor: rest };
          },
          false,
          'bank/clearBankAccountsForVendor'
        ),

      // Pagination actions
      setPaginationForVendor: (vendorId, pagination) =>
        set(
          (state) => ({
            paginationByVendor: {
              ...state.paginationByVendor,
              [vendorId]: {
                ...(state.paginationByVendor[vendorId] || defaultPagination),
                ...pagination,
              },
            },
          }),
          false,
          'bank/setPaginationForVendor'
        ),

      // UI state actions
      setLoading: (isLoading) =>
        set({ isLoading }, false, 'bank/setLoading'),

      setError: (error) =>
        set({ error }, false, 'bank/setError'),

      // Modal actions
      openBankModal: (vendorId, bankId) =>
        set(
          {
            isBankModalOpen: true,
            currentVendorId: vendorId,
            editingBankId: bankId ?? null,
          },
          false,
          'bank/openBankModal'
        ),

      closeBankModal: () =>
        set(
          {
            isBankModalOpen: false,
            currentVendorId: null,
            editingBankId: null,
            currentBankAccount: null,
          },
          false,
          'bank/closeBankModal'
        ),

      // Reset action
      reset: () =>
        set(initialState, false, 'bank/reset'),
    }),
    { name: 'BankStore' }
  )
);

/**
 * Selectors for common queries
 */

// Stable empty array to prevent infinite re-renders
const EMPTY_BANK_ACCOUNTS: BankAccountResponse[] = [];

// Get bank accounts for a specific vendor
export const useBankAccountsForVendor = (vendorId: number) => {
  return useBankStore((state) => state.bankAccountsByVendor[vendorId] || EMPTY_BANK_ACCOUNTS);
};

// Get pagination for a specific vendor
export const useBankPaginationForVendor = (vendorId: number) => {
  return useBankStore(
    (state) => state.paginationByVendor[vendorId] || defaultPagination
  );
};

// Get default bank account for a vendor
export const useDefaultBankAccount = (vendorId: number) => {
  return useBankStore((state) =>
    (state.bankAccountsByVendor[vendorId] || EMPTY_BANK_ACCOUNTS).find((b) => b.isDefault)
  );
};

// Get active bank accounts count for a vendor
export const useActiveBankAccountsCount = (vendorId: number) => {
  return useBankStore((state) =>
    (state.bankAccountsByVendor[vendorId] || EMPTY_BANK_ACCOUNTS).filter((b) => b.isActive).length
  );
};

// Get bank accounts by currency for a vendor
export const useBankAccountsByCurrency = (vendorId: number, currency: string) => {
  return useBankStore((state) =>
    (state.bankAccountsByVendor[vendorId] || EMPTY_BANK_ACCOUNTS).filter((b) => b.currency === currency)
  );
};
